import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { featuredSlides } from '@/mocks/site';
import type { VideoItem } from '@/types/video';
import { getVideoById, typeNameMap } from '@/utils/videos';

interface Slide {
  id: string;
  tagline: string;
  label: string;
  video: VideoItem;
}

export default function HeroCarousel() {
  const slides = useMemo<Slide[]>(
    () =>
      featuredSlides
        .map((s) => {
          const video = getVideoById(s.id);
          return video ? { id: s.id, tagline: s.tagline, label: s.label, video } : null;
        })
        .filter((s): s is Slide => s !== null),
    [],
  );

  const [index, setIndex] = useState(0);

  const go = useCallback(
    (next: number) => {
      if (slides.length === 0) return;
      setIndex(((next % slides.length) + slides.length) % slides.length);
    },
    [slides.length],
  );

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  return (
    <section className="relative w-full h-[400px] md:h-[540px] rounded-xl overflow-hidden bg-background-200">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={slide.video.backdrop}
            alt={`${slide.video.title} 剧照`}
            title={`${slide.video.title} 高清在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40" />
        </div>
      ))}

      <div className="relative z-20 h-full w-full flex items-center px-6 md:px-14">
        <div className="max-w-xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full bg-accent-500 text-background-950 text-xs font-semibold">
              {slides[index].label}
            </span>
            <span className="flex items-center gap-1 text-xs text-foreground-700">
              <i className="ri-star-fill text-accent-400 text-[11px]" />
              {slides[index].video.rating.toFixed(1)} 分 · {typeNameMap[slides[index].video.type]}
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight">
            {slides[index].video.title}
          </h2>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-foreground-700 leading-relaxed max-w-lg">
            {slides[index].tagline}
          </p>
          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slides[index].video.id}`}
              className="flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to={`/category/${slides[index].video.type}`}
              className="flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-background-200/80 text-foreground-900 text-sm font-semibold whitespace-nowrap hover:bg-background-300/80 transition-colors"
            >
              <i className="ri-apps-2-line text-base" />
              更多{typeNameMap[slides[index].video.type]}
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute z-20 left-5 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-950/50 text-foreground-900 hover:bg-background-950/75 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute z-20 right-5 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-950/50 text-foreground-900 hover:bg-background-950/75 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute z-20 bottom-5 left-6 md:left-14 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到 ${slide.video.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-foreground-500/50 hover:bg-foreground-400'
            }`}
          />
        ))}
      </div>
    </section>
  );
}